/**
 * ClickdealerIntegrationViaApiPage — ClickDealer API integration control panel.
 * Forms to register credentials + map offers; read-only panels for credentials,
 * offer mappings, and recent received postbacks. POSTs via dedicated hooks.
 */
import { useState, type FormEvent } from "react";
import {
	useClickdealerCredentialsCreate,
	useClickdealerCredentialsList,
	useClickdealerOfferCreate,
	useClickdealerOffersList,
	useClickdealerPostbacksList,
	useClickdealerStatus,
} from "./hooks";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const PILL_OK =
	"inline-flex items-center gap-1 rounded-[6px] bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-200";
const PILL_OFF =
	"inline-flex items-center gap-1 rounded-[6px] bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600 ring-1 ring-inset ring-zinc-200";

export function ClickdealerIntegrationViaApiPage() {
	const status = useClickdealerStatus();
	const credentials = useClickdealerCredentialsList();
	const offers = useClickdealerOffersList();
	const postbacks = useClickdealerPostbacksList();

	const credMut = useClickdealerCredentialsCreate();
	const offerMut = useClickdealerOfferCreate();

	const [cdAccountId, setCdAccountId] = useState("");
	const [cdApiKey, setCdApiKey] = useState("");
	const [credSaved, setCredSaved] = useState(false);

	const [cdOfferId, setCdOfferId] = useState("");
	const [trkOfferId, setTrkOfferId] = useState("");
	const [offerSaved, setOfferSaved] = useState(false);

	const handleCreds = (e: FormEvent) => {
 e.preventDefault();
 setCredSaved(false);
 credMut.mutate(
 { cd_account_id: cdAccountId.trim(), cd_api_key: cdApiKey.trim() },
 {
 onSuccess: () => {
 setCredSaved(true);
 setCdAccountId("");
 setCdApiKey("");
 },
 },
 );
	};

	const handleOffer = (e: FormEvent) => {
 e.preventDefault();
 setOfferSaved(false);
 offerMut.mutate(
 {
 cd_offer_id: cdOfferId.trim(),
 trackerity_offer_id: trkOfferId.trim(),
 },
 {
 onSuccess: () => {
 setOfferSaved(true);
 setCdOfferId("");
 setTrkOfferId("");
 },
 },
 );
	};

	const isLoading =
 status.isLoading ||
 credentials.isLoading ||
 offers.isLoading ||
 postbacks.isLoading;

	return (
 <main
 data-testid="clickdealer-integration-via-api-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 ClickDealer Integration
 </h1>
 <p className="text-sm text-zinc-600">
 Connect your ClickDealer account, map offers, and review incoming S2S
 postbacks.
 </p>
 <div
 className="mt-1 flex flex-wrap items-center gap-2"
 data-testid="clickdealer-integration-via-api-status"
 >
 <span
 className={status.data?.has_credentials ? PILL_OK : PILL_OFF}
 data-testid="clickdealer-integration-via-api-has-credentials"
 >
 Credentials: {status.data?.has_credentials ? "connected" : "not set"}
 </span>
 <span
 className={status.data?.has_offer_mappings ? PILL_OK : PILL_OFF}
 >
 Offers: {status.data?.has_offer_mappings ? "mapped" : "none"}
 </span>
 <span className={status.data?.has_postbacks ? PILL_OK : PILL_OFF}>
 Postbacks: {status.data?.has_postbacks ? "receiving" : "none"}
 </span>
 </div>
 </header>

 {isLoading ? (
 <p
 data-testid="clickdealer-integration-via-api-loading"
 className="text-sm text-zinc-500"
 >
 Loading ClickDealer integration status.
 </p>
 ) : null}

 <section className="grid gap-6 md:grid-cols-2">
 <form
 onSubmit={handleCreds}
 className={`${CARD} flex flex-col gap-4`}
 data-testid="clickdealer-integration-via-api-credentials-form"
 aria-label="ClickDealer credentials form"
 >
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Register ClickDealer credentials
 </h2>
 <div className="flex flex-col gap-1.5">
 <label htmlFor="cd-account-id" className={LABEL}>
 ClickDealer account ID
 </label>
 <input
 id="cd-account-id"
 type="text"
 required
 className={INPUT}
 value={cdAccountId}
 onChange={(e) => setCdAccountId(e.currentTarget.value)}
 data-testid="clickdealer-integration-via-api-cd-account-id-input"
 />
 </div>
 <div className="flex flex-col gap-1.5">
 <label htmlFor="cd-api-key" className={LABEL}>
 ClickDealer API key
 </label>
 <input
 id="cd-api-key"
 type="password"
 required
 minLength={8}
 className={INPUT}
 value={cdApiKey}
 onChange={(e) => setCdApiKey(e.currentTarget.value)}
 data-testid="clickdealer-integration-via-api-cd-api-key-input"
 />
 <p className="text-xs text-zinc-500">
 Stored as a SHA-256 hash; only a 6-char preview is retained.
 </p>
 </div>
 <div className="flex items-center gap-3">
 <button
 type="submit"
 className={BTN}
 disabled={credMut.isPending}
 data-testid="clickdealer-integration-via-api-credentials-save"
 >
 {credMut.isPending ? "Saving." : "Register"}
 </button>
 {credSaved ? (
 <p className="text-sm text-green-700" role="status">
 Registered.
 </p>
 ) : null}
 {credMut.error ? (
 <p className="text-sm text-red-600" role="alert">
 {credMut.error.message}
 </p>
 ) : null}
 </div>
 </form>

 <form
 onSubmit={handleOffer}
 className={`${CARD} flex flex-col gap-4`}
 data-testid="clickdealer-integration-via-api-offer-form"
 aria-label="ClickDealer offer mapping form"
 >
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Map ClickDealer offer to Trackerity offer
 </h2>
 <div className="flex flex-col gap-1.5">
 <label htmlFor="cd-offer-id" className={LABEL}>
 ClickDealer offer ID
 </label>
 <input
 id="cd-offer-id"
 type="text"
 required
 className={INPUT}
 value={cdOfferId}
 onChange={(e) => setCdOfferId(e.currentTarget.value)}
 data-testid="clickdealer-integration-via-api-cd-offer-id-input"
 />
 </div>
 <div className="flex flex-col gap-1.5">
 <label htmlFor="trk-offer-id" className={LABEL}>
 Trackerity offer ID
 </label>
 <input
 id="trk-offer-id"
 type="text"
 required
 className={INPUT}
 value={trkOfferId}
 onChange={(e) => setTrkOfferId(e.currentTarget.value)}
 data-testid="clickdealer-integration-via-api-trk-offer-id-input"
 />
 </div>
 <div className="flex items-center gap-3">
 <button
 type="submit"
 className={BTN}
 disabled={offerMut.isPending}
 data-testid="clickdealer-integration-via-api-offer-save"
 >
 {offerMut.isPending ? "Saving." : "Map offer"}
 </button>
 {offerSaved ? (
 <p className="text-sm text-green-700" role="status">
 Mapped.
 </p>
 ) : null}
 {offerMut.error ? (
 <p className="text-sm text-red-600" role="alert">
 {offerMut.error.message}
 </p>
 ) : null}
 </div>
 </form>
 </section>

 <section className={CARD} data-testid="clickdealer-integration-via-api-credentials-list">
 <h2 className="text-green-700 font-semibold mb-3 text-base font-semibold text-zinc-900">
 Registered credentials
 </h2>
 {credentials.data && credentials.data.items.length === 0 ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="clickdealer-integration-via-api-credentials-empty"
 >
 No credentials registered yet.
 </p>
 ) : null}
 {credentials.data && credentials.data.items.length > 0 ? (
 <ul className="divide-y divide-zinc-100">
 {credentials.data.items.map((c) => (
 <li
 key={c.id}
 className="flex items-center justify-between py-2 text-sm"
 >
 <span className="font-mono text-zinc-800">{c.cd_account_id}</span>
 <span className="font-mono text-xs text-zinc-500">
 key: {c.cd_api_key_preview}…
 </span>
 <span className={c.status === "active" ? PILL_OK : PILL_OFF}>
 {c.status}
 </span>
 </li>
 ))}
 </ul>
 ) : null}
 </section>

 <section className={CARD} data-testid="clickdealer-integration-via-api-offers-list">
 <h2 className="text-green-700 font-semibold mb-3 text-base font-semibold text-zinc-900">
 Offer mappings
 </h2>
 {offers.data && offers.data.items.length === 0 ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="clickdealer-integration-via-api-offers-empty"
 >
 No offers mapped yet.
 </p>
 ) : null}
 {offers.data && offers.data.items.length > 0 ? (
 <ul className="divide-y divide-zinc-100">
 {offers.data.items.map((o) => (
 <li
 key={o.id}
 className="flex items-center justify-between py-2 text-sm"
 >
 <span className="font-mono text-zinc-800">{o.cd_offer_id}</span>
 <span className="text-xs text-zinc-400">→</span>
 <span className="font-mono text-zinc-800">
 {o.trackerity_offer_id}
 </span>
 </li>
 ))}
 </ul>
 ) : null}
 </section>

 <section className={CARD} data-testid="clickdealer-integration-via-api-postbacks-list">
 <h2 className="text-green-700 font-semibold mb-3 text-base font-semibold text-zinc-900">
 Recent postbacks
 </h2>
 {postbacks.data && postbacks.data.items.length === 0 ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="clickdealer-integration-via-api-postbacks-empty"
 >
 No postbacks received yet.
 </p>
 ) : null}
 {postbacks.data && postbacks.data.items.length > 0 ? (
 <ul className="divide-y divide-zinc-100">
 {postbacks.data.items.slice(0, 20).map((p) => (
 <li
 key={p.id}
 className="flex items-center justify-between gap-3 py-2 text-sm"
 >
 <span className="font-mono text-xs text-zinc-600">
 {p.cd_click_id}
 </span>
 <span className="font-mono text-xs text-zinc-500">
 {p.cd_offer_id}
 </span>
 <span className="text-xs text-zinc-700">
 ${(p.payout_cents / 100).toFixed(2)}
 </span>
 <span
 className={
 p.status === "approved" || p.status === "ok"
 ? PILL_OK
 : PILL_OFF
 }
 >
 {p.status}
 </span>
 </li>
 ))}
 </ul>
 ) : null}
 </section>
 </main>
	);
}

export default ClickdealerIntegrationViaApiPage;
